import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchSentEmailRecipients,
  fetchUserSentEmailTimeline,
  type SentEmailRecipientApi,
  type SentEmailTimelineEmailApi,
} from "../../../../api/campaigns";
import { SectionCard } from "./SectionLayout";
import type { CampaignSummary } from "../types";

const RECIPIENTS_LIMIT = 20;
const TIMELINE_LIMIT = 5;
const DEFAULT_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone ?? "America/Los_Angeles";

type EmailStatus =
  | "scheduled"
  | "sent"
  | "opened"
  | "clicked"
  | "replied"
  | "converted"
  | "bounced"
  | "complained";

const statusBadgeClass: Record<EmailStatus, string> = {
  scheduled: "bg-gray-100 text-gray-600",
  sent: "bg-blue-100 text-blue-600",
  opened: "bg-emerald-100 text-emerald-600",
  clicked: "bg-purple-100 text-purple-600",
  replied: "bg-amber-100 text-amber-600",
  converted: "bg-[var(--primary-color)]/10 text-[var(--primary-color)]",
  bounced: "bg-red-100 text-red-600",
  complained: "bg-orange-100 text-orange-600",
};

const getStatusClass = (status?: string) => {
  if (!status) return statusBadgeClass.sent;
  const normalized = status.toLowerCase() as EmailStatus;
  return statusBadgeClass[normalized] ?? statusBadgeClass.sent;
};

const resolveTimelineStatus = (entry: SentEmailTimelineEmailApi): EmailStatus => {
  // Priority: replied > clicked > opened > sent
  const events = entry.events ?? [];
  const hasReplied = events.some(
    (event) => event.type?.toLowerCase() === "replied"
  );
  if (hasReplied) return "replied";
  const hasClicked = events.some(
    (event) => event.type?.toLowerCase() === "clicked"
  );
  if (hasClicked) return "clicked";
  const hasOpened = events.some(
    (event) => event.type?.toLowerCase() === "opened"
  );
  if (hasOpened) return "opened";
  return "sent";
};

const formatDatetime = (value?: string, timeZone?: string) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
};

const formatSequenceLabel = (index?: number) => {
  if (typeof index !== "number") return "Automated send";
  return `Sequence email #${index + 1}`;
};

const formatThreadAuthor = (role: string, fallback?: string): string => {
  const normalized = role.toLowerCase();
  if (normalized === "assistant") return fallback ?? "Assistant";
  if (normalized === "system") return "System";
  return "User";
};

type ParsedEmailPayload = {
  subject?: string;
  html?: string;
  text?: string;
};

const parseEmailPayload = (raw?: string): ParsedEmailPayload | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const subject =
      typeof parsed.subject === "string" ? parsed.subject : undefined;
    const html = typeof parsed.html === "string" ? parsed.html : undefined;
    const text = typeof parsed.text === "string" ? parsed.text : undefined;

    if (!subject && !html && !text) {
      return null;
    }

    return { subject, html, text };
  } catch {
    return null;
  }
};

export interface UserHistorySectionProps {
  campaignOptions?: CampaignSummary[];
}

const ThreadMessageContent: React.FC<{ content: string }> = ({ content }) => {
  const parsed = parseEmailPayload(content);

  if (!parsed) {
    return (
      <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
        {content}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2 text-sm text-gray-700">
      {parsed.subject ? (
        <div className="font-semibold text-gray-900">{parsed.subject}</div>
      ) : null}
      {parsed.html ? (
        <div
          className="rounded-xl bg-gray-50 px-3 py-2 text-sm leading-relaxed text-gray-700"
          dangerouslySetInnerHTML={{ __html: parsed.html }}
        />
      ) : parsed.text ? (
        <div className="whitespace-pre-wrap leading-relaxed">
          {parsed.text}
        </div>
      ) : null}
    </div>
  );
};

const EventTimeline: React.FC<{
  events: SentEmailTimelineEmailApi["events"];
  timezone?: string;
}> = ({ events, timezone }) => {
  if (!events?.length) {
    return (
      <div className="text-xs text-gray-500">No events logged for this email.</div>
    );
  }

  return (
    <div className="pl-4 relative">
      <span className="absolute left-0 top-2 h-full w-px bg-gray-200" />
      <div className="space-y-4">
        {events.map((event, index) => (
          <div
            key={`${event.type}-${event.timestamp}-${index}`}
            className="relative pl-6"
          >
            <span className="absolute left-0 top-1.5 h-3 w-3 rounded-full bg-[var(--primary-color)]" />
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {event.type}
            </div>
            <div className="text-sm text-gray-900">
              {formatDatetime(event.timestamp, timezone)}
            </div>
            {event.metadata ? (
              <div className="text-xs text-gray-500">
                {Object.entries(event.metadata)
                  .map(([key, value]) => `${key}: ${String(value)}`)
                  .join(" · ")}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

type PaginationInfo = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export interface UserHistorySectionProps {
  campaignOptions?: CampaignSummary[];
}

export const UserHistorySection: React.FC<UserHistorySectionProps> = ({
  campaignOptions = [],
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recipients, setRecipients] = useState<SentEmailRecipientApi[]>([]);
  const [recipientPage, setRecipientPage] = useState(1);
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<
    "latest" | "opened" | "replied" | "sent"
  >("latest");
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [timelinePage, setTimelinePage] = useState(1);
  const [timelineEmails, setTimelineEmails] = useState<
    SentEmailTimelineEmailApi[]
  >([]);
  const [timelinePagination, setTimelinePagination] = useState<
    PaginationInfo | null
  >(null);
  const [timelineTimezone, setTimelineTimezone] = useState(DEFAULT_TIMEZONE);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setRecipientPage(1);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setRecipientPage(1);
  }, [campaignFilter, statusFilter, debouncedSearch]);

  const selectUser = useCallback((userId?: string) => {
    setSelectedUserId(userId ?? "");
    setTimelineEmails([]);
    setTimelinePagination(null);
    setTimelinePage(1);
    setTimelineError(null);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setRecipientsLoading(true);
    setRecipientError(null);

    fetchSentEmailRecipients({
      search: debouncedSearch || undefined,
      campaignId: campaignFilter === "all" ? undefined : campaignFilter,
      limit: 10000, // Fetch all users for client-side filtering
      page: 1,
    })
      .then((response) => {
        if (!isMounted) return;
        const userResults = response.users ?? [];
        setRecipients(userResults);

        if (userResults.length) {
          const stillVisible = userResults.some(
            (user) => user.userId === selectedUserId
          );
          if (!stillVisible) {
            selectUser(userResults[0].userId);
          }
        } else {
          if (selectedUserId) {
            selectUser(undefined);
          }
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error("Failed to load sent email recipients", error);
        toast.error("Failed to load sent email recipients.");
        setRecipients([]);
        setRecipientError(
          error instanceof Error
            ? error.message
            : "Unable to load sent Email recipients"
        );
      })
      .finally(() => {
        if (isMounted) {
          setRecipientsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [campaignFilter, debouncedSearch, selectUser, selectedUserId]);

  useEffect(() => {
    if (!selectedUserId) return;

    let isMounted = true;
    setTimelineLoading(true);

    fetchUserSentEmailTimeline(selectedUserId, {
      page: timelinePage,
      campaignId: campaignFilter === "all" ? undefined : campaignFilter,
      timezone: DEFAULT_TIMEZONE,
    })
      .then((response) => {
        if (!isMounted) return;
        setTimelineTimezone(response.timezone ?? DEFAULT_TIMEZONE);
        setTimelinePagination(response.pagination ?? null);
        const newEmails = response.emails ?? [];
        setTimelineEmails((prev) =>
          timelinePage === 1 ? newEmails : [...prev, ...newEmails]
        );
        setTimelineError(null);
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error(
          `Failed to load sent email timeline for ${selectedUserId}`,
          error
        );
        toast.error("Failed to load email timeline.");
        setTimelineError(
          error instanceof Error ? error.message : "Unable to load timeline"
        );
        if (timelinePage === 1) {
          setTimelineEmails([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setTimelineLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [campaignFilter, selectedUserId, timelinePage]);

  useEffect(() => {
    setTimelinePage(1);
    setTimelineEmails([]);
    setTimelinePagination(null);
  }, [campaignFilter, selectedUserId]);

  const selectedRecipient = useMemo(
    () => recipients.find((recipient) => recipient.userId === selectedUserId),
    [recipients, selectedUserId]
  );

  const filteredRecipients = useMemo(() => {
    const matchesEngagement = (recipient: SentEmailRecipientApi) => {
      const opened = recipient.hasOpened ?? (recipient.openedCount ?? 0) > 0;
      const replied = recipient.hasReplied ?? (recipient.repliedCount ?? 0) > 0;

      switch (statusFilter) {
        case "opened":
          return opened;
        case "replied":
          return replied;
        case "sent":
          return !opened && !replied;
        case "latest":
        default:
          return true;
      }
    };

    const matchesCampaign = (recipient: SentEmailRecipientApi) => {
      if (campaignFilter === "all") return true;
      if (recipient.campaigns?.length) {
        return recipient.campaigns.some(
          (campaign) => campaign.campaignId === campaignFilter
        );
      }
      // If campaign list not provided, assume server-side filtering handled it.
      return true;
    };

    return recipients.filter(
      (recipient) => matchesEngagement(recipient) && matchesCampaign(recipient)
    );
  }, [campaignFilter, recipients, statusFilter]);

  const sortedRecipients = useMemo(() => {
    const fallback = (value?: string) =>
      value ? new Date(value).getTime() : Number.NEGATIVE_INFINITY;
    return [...filteredRecipients].sort((a, b) => {
      const aTime = fallback(a.lastSentAt);
      const bTime = fallback(b.lastSentAt);
      if (aTime !== bTime) return bTime - aTime;
      if ((b.totalEmails ?? 0) !== (a.totalEmails ?? 0)) {
        return (b.totalEmails ?? 0) - (a.totalEmails ?? 0);
      }
      return a.email.localeCompare(b.email);
    });
  }, [filteredRecipients]);

  // Client-side pagination
  const paginatedRecipients = useMemo(() => {
    const startIndex = (recipientPage - 1) * RECIPIENTS_LIMIT;
    return sortedRecipients.slice(startIndex, startIndex + RECIPIENTS_LIMIT);
  }, [sortedRecipients, recipientPage]);

  const totalRecipientPages = Math.max(1, Math.ceil(sortedRecipients.length / RECIPIENTS_LIMIT));

  const canLoadMoreTimeline = useMemo(() => {
    if (!timelinePagination) return false;
    if (timelinePagination.totalPages <= 1) return false;
    return timelinePagination.page < timelinePagination.totalPages;
  }, [timelinePagination]);

  return (
    <SectionCard
      title="User Email History"
      description="Review delivered campaigns, engagement events, and conversation excerpts for every user."
    >
      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
        <div>
          <div className="space-y-3">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Search users
              </span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Enter email address"
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              />
            </label>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Campaign
                </span>
                <select
                  value={campaignFilter}
                  onChange={(event) => setCampaignFilter(event.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                >
                  <option value="all">All campaigns</option>
                  {campaignOptions.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                      {campaign.status === "active" ? " (Live)" : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Engagement
                </span>
                <select
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(
                      event.target.value as
                        | "latest"
                        | "opened"
                        | "replied"
                        | "sent"
                    )
                  }
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                >
                  <option value="latest">Latest activity</option>
                  <option value="opened">Opened</option>
                  <option value="replied">Replied</option>
                  <option value="sent">Sent only</option>
                </select>
              </label>
            </div>
          </div>

          <div className="mt-4 space-y-2 max-h-[480px] overflow-y-auto pr-2">
            {recipientsLoading && recipients.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-center text-xs text-gray-500">
                Loading recipients...
              </div>
            ) : null}

            {!recipientsLoading && paginatedRecipients.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-center text-xs text-gray-500">
                {debouncedSearch
                  ? `No users match "${debouncedSearch}".`
                  : "No sent emails found yet."}
              </div>
            ) : null}

            {recipientError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
                {recipientError}
              </div>
            ) : null}

            {paginatedRecipients.map((recipient) => (
              <button
                key={recipient.userId}
                onClick={() => {
                  if (recipient.userId !== selectedUserId) {
                    selectUser(recipient.userId);
                  }
                }}
                type="button"
                className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition ${
                  recipient.userId === selectedUserId
                    ? "border-[var(--primary-color)] bg-[var(--primary-color)]/10 text-[var(--primary-color)]"
                    : "border-gray-200 hover:border-[var(--primary-color)] hover:bg-[var(--primary-color)]/10 text-gray-700"
                }`}
              >
                <div className="font-semibold break-all">{recipient.email}</div>
                <div className="text-xs text-gray-500">
                  {recipient.totalEmails ?? 0} emails · {recipient.threadCount ?? 0} threads
                </div>
                {recipient.lastSentAt ? (
                  <div className="text-[11px] text-gray-400">
                    Last sent {formatDatetime(recipient.lastSentAt)}
                  </div>
                ) : null}
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
            <span>
              Page {recipientPage} of {totalRecipientPages} ({sortedRecipients.length} total users)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRecipientPage((page) => Math.max(1, page - 1))}
                disabled={recipientPage <= 1}
                className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() =>
                  setRecipientPage((page) =>
                    page >= totalRecipientPages ? page : page + 1
                  )
                }
                disabled={recipientPage >= totalRecipientPages}
                className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          {selectedUserId ? (
            <div className="space-y-6">
              {selectedRecipient ? (
                <div>
                  <div className="text-lg font-semibold text-gray-900 break-all">
                    {selectedRecipient.email}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedRecipient.totalEmails ?? 0} emails sent · {selectedRecipient.threadCount ?? 0} threads · {selectedRecipient.campaignCount ?? 0} campaigns
                  </div>
                  {selectedRecipient.lastSentAt ? (
                    <div className="text-xs text-gray-400">
                      Latest send {formatDatetime(selectedRecipient.lastSentAt)}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="text-xs text-gray-500">
                Timestamps localized to {timelineTimezone}
              </div>

              {timelineError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {timelineError}
                </div>
              ) : null}

              {timelineLoading && timelineEmails.length === 0 ? (
                <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-gray-500">
                  Loading timeline...
                </div>
              ) : null}

              {!timelineLoading && timelineEmails.length === 0 && !timelineError ? (
                <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-gray-500">
                  No emails recorded for this user yet.
                </div>
              ) : null}

              {timelineEmails.length ? (
                <div className="space-y-4">
                  {timelineEmails.map((entry) => (
                    <div
                      key={entry.emailId}
                      className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
                    >
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {entry.subject ?? "(No subject)"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {[
                              entry.characterName,
                              formatSequenceLabel(entry.sequenceIndex),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                        {(() => {
                          const status = resolveTimelineStatus(entry);
                          return (
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize ${getStatusClass(status)}`}
                            >
                              {status}
                            </span>
                          );
                        })()}
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        Sent {formatDatetime(entry.sentAtLocal ?? entry.sentAt, timelineTimezone)}
                      </div>

                      <div className="mt-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Timeline
                        </div>
                        <EventTimeline events={entry.events} timezone={timelineTimezone} />
                      </div>

                      <div className="mt-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Thread
                        </div>
                        {entry.thread?.length ? (
                          <div className="mt-2 space-y-3">
                            {entry.thread.map((message, index) => (
                              <div
                                key={`${message.timestamp}-${index}`}
                                className="rounded-2xl border border-gray-200 bg-white px-4 py-3"
                              >
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                  <span className="font-semibold text-gray-700">
                                    {formatThreadAuthor(message.role, entry.characterName)}
                                  </span>
                                  <span>{formatDatetime(message.timestamp, timelineTimezone)}</span>
                                </div>
                                <ThreadMessageContent content={message.content} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-gray-500">
                            No chat excerpts were captured for this email.
                          </div>
                        )}
                      </div>

                      {selectedUserId ? (
                        <div className="mt-4">
                          <a
                            href={`/admin/messages/${encodeURIComponent(selectedUserId)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-xs font-semibold text-[var(--primary-color)] hover:underline"
                          >
                            Open conversation in a new tab →
                          </a>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {canLoadMoreTimeline ? (
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={() => setTimelinePage((page) => page + 1)}
                    disabled={timelineLoading}
                    className="inline-flex items-center rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] disabled:opacity-50"
                  >
                    {timelineLoading ? "Loading..." : "Load older emails"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-gray-500">
              Select a user to view their history.
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
};
